import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TapestryLayoutService } from '@predictiveindex/tapestry';
import {
    CurrentModuleOptions,
    Inspire2Link,
    PI,
    UserAccessSoftwareModule,
    performLoginPath
} from '../../constants-types';
import { InternalModuleRequest } from '../../core-data-model';
import { InternalActivationsCardContent } from '../../core-data-model/InternalActivations.model';
import { CompaniesDataService, ModuleUserApi } from '../../core-services';
import { GlobalNavService } from '../../core-ui/global-nav/global-nav.service';
import {
    FeatureFlags,
    FeatureFlagsService,
    InitDataService,
    ModuleAccessService
} from '../../system-services';
import { TranslationService } from '../../translations';

@Component({
    selector: 'internal-activations-page',
    template: `<div class="piApp--Content--globalnav pageWidthLimiter">
        @if (isUnlockingModule) {
            <div class="load-container">
                <tapestry-spinner
                    [altText]="
                        'internal-activations.loading-alt-text'
                            | translateFormat: softwareModule
                    "
                    spinnerSize="lg"
                ></tapestry-spinner>
            </div>
        }
        <div
            class="internal-activations-header-background"
            [style.background-image]="bgImgSrc"
        >
            <div
                [ngClass]="
                    layout.isMobile
                        ? 'internal-activations-header-text-section-mobile section-spacing'
                        : 'internal-activations-header-text-section section-spacing'
                "
            >
                <div class="left-spacing">
                    <div class="tapestry-title title--lg title-spacing">
                        {{ titleText | translate }}
                    </div>
                    <div
                        class="tapestry-body body--md section-spacing subTitle--innerText"
                        [innerHTML]="subtitleText"
                    ></div>
                    @if (isExplainerButtonEnabled && isPI2OrgAdmin) {
                        <tapestry-button
                            [text]="buttonText"
                            trackingName="internal_activation_header_button"
                            (buttonClicked)="activateModuleOnClick()"
                            [disabled]="isUnlockingModule"
                        ></tapestry-button>
                    }
                    @if (isExplainerButtonEnabled && !isPI2OrgAdmin) {
                        <tapestry-button
                            [text]="buttonText"
                            trackingName="internal_activation_header_button--non-admin"
                        ></tapestry-button>
                    }
                    @if (!isExplainerButtonEnabled && isPerformModule) {
                        <tapestry-inline-message-box
                            type="tapestry-inline-message-box--success"
                            [content]="
                                'internalActivations.perform.activated'
                                    | translate
                            "
                        >
                        </tapestry-inline-message-box>
                    }
                    @if (!isPerformModule) {
                        <div>
                            <div
                                class="tapestry-body body--xs section-spacing italicText"
                                [ngStyle]="{
                                    visibility: isHireModule ? 'hidden' : ''
                                }"
                            >
                                {{ vmDescription | translate }}
                            </div>
                            <div class="tapestry-body body--sm caps">
                                {{
                                    'internalActivations.logo.prefix'
                                        | translate
                                }}
                            </div>
                            <div>
                                <img
                                    src="/platformAssets/internalActivations/logos.png"
                                />
                            </div>
                        </div>
                    }
                </div>
                <div>
                    <img
                        [ngClass]="
                            isDiagnoseModule
                                ? 'internal-activation-hero-image-width'
                                : ''
                        "
                        [attr.src]="heroImgSrc"
                    />
                </div>
            </div>
        </div>
        <div class="internal-activations-footer-background">
            <div
                [ngClass]="
                    layout.isMobile
                        ? 'content-container-mobile'
                        : 'content-container'
                "
            >
                <div
                    class="tapestry-title title--xs internal-activations-footer-title"
                >
                    {{ 'internalActivations.footer.title' | translate }}
                </div>
                <div class="internal-activations-card-container">
                    @for (card of cards; track card) {
                        <internal-activations-card
                            [cardContent]="card"
                            class="internal-activations-footer-card"
                            [isDiagnosePage]="isDiagnoseModule"
                        ></internal-activations-card>
                    }
                </div>
                @if (isExplainerButtonEnabled) {
                    <tapestry-button
                        [text]="buttonText"
                        trackingName="internal_activation_footer_button"
                        (buttonClicked)="activateModuleOnClick()"
                        [disabled]="isUnlockingModule"
                        class="internal-activations-footer-btn"
                    ></tapestry-button>
                }
            </div>
        </div>
    </div>`,
    styleUrls: ['./InternalActivations.page.less'],
    standalone: false
})
export class InternalActivationsPageComponent implements OnInit {
    bgImgSrc: string;
    buttonText: string;
    heroImgSrc: string;
    isUnlockingModule: boolean;
    isExplainerButtonEnabled = false;
    softwareModule: string;
    subtitleText: string;
    titleText: string;
    vmDescription: string;
    cards: InternalActivationsCardContent[] = [];
    isHireModule: boolean;
    isDiagnoseModule: boolean;
    isPerformModule: boolean;
    isPI2OrgAdmin: boolean;

    constructor(
        public TR: TranslationService,
        public layout: TapestryLayoutService,
        private companyService: CompaniesDataService,
        private featureFlags: FeatureFlagsService,
        private initDataService: InitDataService,
        private moduleAccessService: ModuleAccessService,
        private globalNavService: GlobalNavService,
        private moduleUserApi: ModuleUserApi,
        private route: ActivatedRoute,
        private readonly router: Router
    ) {}

    ngOnInit() {
        this.isPI2OrgAdmin = this.initDataService.isPI2OrgAdmin();
        this.route.paramMap.subscribe((params) => {
            this.softwareModule = params.get('module')?.toLowerCase();
            this.isHireModule =
                this.softwareModule === CurrentModuleOptions.Hire.toLowerCase();
            this.isDiagnoseModule =
                this.softwareModule ===
                CurrentModuleOptions.Diagnose.toLowerCase();
            this.isPerformModule =
                this.softwareModule ===
                CurrentModuleOptions.Perform.toLowerCase();
            this.isExplainerButtonEnabled = this.getExplainerButtonFlag(
                this.softwareModule
            );
            this.setContent();
        });
    }

    async activateModuleOnClick() {
        this.isUnlockingModule = true;

        await this.refreshModuleAccess();
    }

    refreshModuleAccess(): Promise<void> {
        const internalModuleRequest: InternalModuleRequest = {
            companyName: this.companyService.currentCompanyName,
            module: this.getModuleToActivate(),
            userId: this.initDataService.user.userGuid,
            companyId: this.companyService.currentCompany.id
        };

        const moduleActivationPromise =
            // If requested module is Perform, then use Perform activation endpoint
            // within account management api service
            internalModuleRequest.module === UserAccessSoftwareModule.Perform
                ? this.moduleUserApi.activateFreeLegacyPerform(
                      internalModuleRequest
                  )
                : this.moduleUserApi.internalActivate(internalModuleRequest);

        return moduleActivationPromise
            .then(() => {
                Promise.all([
                    this.moduleAccessService.fetchConfig(),
                    this.initDataService.refreshUserPermissions()
                ]);
            })
            .finally(() => {
                this.isUnlockingModule = false;
                this.globalNavService.updateAvailableModulesForNavigation();
                this.redirectToModulePage();
            })
            .catch(() => {
                this.isUnlockingModule = false;
                this.router.navigate(['/error']);
            });
    }

    getCardImage(cardNumber: number): string {
        return `/platformAssets/internalActivations/${this.softwareModule}${cardNumber}.svg`;
    }

    getCardsContent(): InternalActivationsCardContent[] {
        const cards: number[] = [1, 2, 3];

        const content: InternalActivationsCardContent[] = [];

        cards.forEach((n) => {
            content.push({
                title: `internalActivations.cardTitle.${this.softwareModule}-${n}`,
                subtitle: `internalActivations.cardSubtitle.${this.softwareModule}-${n}`,
                imageSource: this.getCardImage(n),
                step: `engage-runsurvey.label-step${n}`
            });
        });
        return content;
    }

    setContent() {
        this.cards = this.getCardsContent();
        this.bgImgSrc = `url('/platformAssets/internalActivations/bg-${this.softwareModule}.png')`;
        this.buttonText = this.isPI2OrgAdmin
            ? this.TR.key(`internalActivations.button.${this.softwareModule}`)
            : this.TR.key('internal-activation.button.non-admin');
        this.heroImgSrc = `/platformAssets/internalActivations/${this.softwareModule}-hero.png`;
        this.subtitleText = this.TR.key(
            `internalActivations.page.subtitle.${this.softwareModule}`
        );
        this.titleText = `internalActivations.page.title.${this.softwareModule}`;
        this.vmDescription = `internalActivations.valueMetrics.${this.softwareModule}`;
    }

    getModuleToActivate(): UserAccessSoftwareModule {
        switch (this.softwareModule) {
            case 'design':
                return UserAccessSoftwareModule.Design;
            case 'hire':
                return UserAccessSoftwareModule.Hire;
            case 'diagnose':
                return UserAccessSoftwareModule.Diagnose;
            case 'inspire':
                return UserAccessSoftwareModule.Inspire;
            case 'perform':
                return UserAccessSoftwareModule.Perform;
            default:
                return null;
        }
    }

    private getExplainerButtonFlag(softwareModule: string): boolean {
        switch (softwareModule) {
            case 'design':
                return this.featureFlags.getByFlag(
                    FeatureFlags.enableDesignExplainerButton
                );
            case 'hire':
                return this.featureFlags.getByFlag(
                    FeatureFlags.enableHireExplainerButton
                );
            case 'diagnose':
                return this.featureFlags.getByFlag(
                    FeatureFlags.enableDiagnoseExplainerButton
                );
            case 'inspire':
                return true;
            case 'perform':
                return this.featureFlags.getByFlag(
                    FeatureFlags.enablePerformInternalActivation
                );
            default:
                return false;
        }
    }

    private redirectToModulePage(): void {
        if (this.softwareModule === 'inspire') {
            this.router.navigate([`${Inspire2Link}`]);
        } else if (this.softwareModule === 'perform') {
            this.isExplainerButtonEnabled = false;
            window.open(PI.appSettings.performUrl + performLoginPath, '_blank');
        } else {
            this.router.navigate([`/${this.softwareModule}`]);
        }
    }
}
